<template>
  <div
    :class="[
      'add-new-popup z-[999] fixed transition-all inset-0 overflow-x-hidden overflow-y-auto lg:py-[20px]',
      { active: isActive },
    ]"
  >
    <div
      class="popup-dialog flex transition-all max-w-[550px] min-h-full items-center mx-auto"
    >
      <div
        class="trezo-card w-full bg-white dark:bg-[#0c1427] p-[20px] md:p-[25px] rounded-md shadow-lg"
      >
        <div
          class="trezo-card-header bg-blue-500 mb-[20px] md:mb-[25px] flex items-center justify-between -mx-[20px] md:-mx-[25px] -mt-[20px] md:-mt-[25px] p-[20px] md:p-[25px] rounded-t-md"
        >
          <div class="trezo-card-title">
            <h5 class="mb-0 text-white font-bold flex items-center">
              <i class="ri-time-line mr-2 text-xl"></i>
              {{ directT('calendar.editWorkingHours') }}
            </h5>
          </div>
          <div class="trezo-card-subtitle">
            <button
              type="button"
              class="text-[23px] transition-all leading-none text-white hover:text-gray-200"
              @click="closeModal"
            >
              <i class="ri-close-fill"></i>
            </button>
          </div>
        </div>
        <div class="trezo-card-content pb-[20px] md:pb-[25px] max-h-[60vh] overflow-y-auto px-1">
          <div v-if="loading" class="text-center my-4">
            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
          </div>
          <div v-else class="space-y-4">
            <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">
              {{ directT('calendar.workingHours.setAvailabilityDescription') }}
            </p>
            
            <div class="mb-5 bg-gray-50 dark:bg-[#15203c] p-4 rounded-lg border-l-4 shadow-sm transition-all hover:shadow-md" 
                v-for="(day, index) in days" 
                :key="index"
                :class="workingHours[`${day.key}_available`] ? 'border-l-green-500' : 'border-l-gray-300'">
              
              <div class="flex items-center justify-between mb-2">
                <div class="flex items-center">
                  <div class="relative inline-block">
                    <input 
                      class="hidden" 
                      type="checkbox" 
                      :id="`available_${day.key}`"
                      v-model="workingHours[`${day.key}_available`]"
                    >
                    <label 
                      class="flex items-center cursor-pointer text-base font-medium text-gray-700 dark:text-gray-300"
                      :for="`available_${day.key}`"
                    >
                      <div class="relative mr-3 w-10 h-5 bg-gray-200 rounded-full" :class="{ 'bg-green-500': workingHours[`${day.key}_available`] }">
                        <div class="absolute top-[2px] bg-white rounded-full h-4 w-4 transition-all" 
                             :style="{ left: workingHours[`${day.key}_available`] ? '22px' : '2px' }">
                        </div>
                      </div>
                      <div class="flex items-center">
                        <span class="text-lg">{{ day.label }}</span>
                        <i class="ri-calendar-line ml-2 text-primary-500" v-if="workingHours[`${day.key}_available`]"></i>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 transition-all" v-if="workingHours[`${day.key}_available`]">
                <div class="mb-2">
                  <label :for="`${day.key}_start`" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                    <i class="ri-time-line mr-1 text-primary-500"></i> {{ directT('calendar.workingHours.startTime') }}
                  </label>
                  <input 
                    type="time" 
                    class="bg-white border border-gray-200 rounded-md w-full px-3 py-2.5 text-black dark:bg-[#1a2544] dark:text-white dark:border-[#172036] focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" 
                    :id="`${day.key}_start`" 
                    v-model="workingHours[`${day.key}_start_time`]"
                  >
                </div>
                <div class="mb-2">
                  <label :for="`${day.key}_end`" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                    <i class="ri-time-line mr-1 text-primary-500"></i> {{ directT('calendar.workingHours.endTime') }}
                  </label>
                  <input 
                    type="time" 
                    class="bg-white border border-gray-200 rounded-md w-full px-3 py-2.5 text-black dark:bg-[#1a2544] dark:text-white dark:border-[#172036] focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" 
                    :id="`${day.key}_end`" 
                    v-model="workingHours[`${day.key}_end_time`]"
                  >
                </div>
                <div class="sm:col-span-2 text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center">
                  <i class="ri-information-line mr-1"></i>
                  {{ directT('calendar.workingHours.workingHours') }}: {{ formatWorkingHours(workingHours[`${day.key}_start_time`], workingHours[`${day.key}_end_time`]) }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          class="trezo-card-footer flex flex-col sm:flex-row items-center justify-between -mx-[20px] md:-mx-[25px] px-[20px] md:px-[25px] pt-[20px] md:pt-[25px] border-t border-gray-100 dark:border-[#172036]"
        >
          <button
            class="w-full sm:w-auto mb-2 sm:mb-0 inline-block py-[10px] px-[30px] bg-red-500 text-white transition-all hover:bg-red-600 rounded-md border border-red-500 hover:border-red-600 order-2 sm:order-1 flex justify-center items-center"
            type="button"
            @click="closeModal"
            :disabled="loading"
          >
            <i class="ri-close-line mr-1"></i> {{ directT('calendar.workingHours.cancel') }}
          </button>
          <button
            class="w-full sm:w-auto mb-2 sm:mb-0 inline-block py-[10px] px-[30px] bg-blue-500 text-white transition-all hover:bg-blue-600 rounded-md border-none shadow-md hover:shadow-lg order-1 sm:order-2 flex justify-center items-center"
            type="button"
            @click="saveWorkingHours"
            :disabled="loading"
          >
            <i class="ri-save-line mr-1"></i> {{ loading ? directT('calendar.saving') : directT('calendar.saveChanges') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed, onUnmounted } from 'vue';
import { supabase } from '@/lib/supabaseClient';
import { useUserStore } from '@/store/user';
import { useI18n } from 'vue-i18n';
import en from '@/i18n/locales/en.json';
import ar from '@/i18n/locales/ar.json';
import he from '@/i18n/locales/he.json';

export default {
  name: 'EditWorkingHoursModal',
  props: {
    isActive: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    // Get i18n directly from Vue I18n
    const { locale } = useI18n();

    const workingHours = ref({
      sunday_available: false,
      sunday_start_time: '09:00',
      sunday_end_time: '17:00',
      monday_available: false,
      monday_start_time: '09:00',
      monday_end_time: '17:00',
      tuesday_available: false,
      tuesday_start_time: '09:00',
      tuesday_end_time: '17:00',
      wednesday_available: false,
      wednesday_start_time: '09:00',
      wednesday_end_time: '17:00',
      thursday_available: false,
      thursday_start_time: '09:00',
      thursday_end_time: '17:00',
      friday_available: false,
      friday_start_time: '09:00',
      friday_end_time: '17:00',
      saturday_available: false,
      saturday_start_time: '09:00',
      saturday_end_time: '17:00',
    });

    // Translation function
    const directT = (key) => {
      // Get current locale directly from Vue-i18n
      const currentLocale = locale.value;
      
      // Create a helper function to get a nested property from an object using a dot-notation string
      const getNestedProperty = (obj, path) => {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj);
      };
      
      // First try current locale
      let translations;
      if (currentLocale === 'ar') {
        translations = ar;
      } else if (currentLocale === 'he') {
        translations = he;
      } else {
        translations = en;
      }
      
      // Try to get translation from current locale
      const translation = getNestedProperty(translations, key);
      
      // If not found, fallback to English
      if (!translation && currentLocale !== 'en') {
        return getNestedProperty(en, key) || key;
      }
      
      return translation || key;
    };

    const loading = ref(false);
    const userId = ref(null);
    const orgId = ref(null);
    const userStore = useUserStore();

    // Get current locale for directional formatting
    const isRtl = computed(() => ['ar', 'he'].includes(locale.value));

    const formatWorkingHours = (startTime, endTime) => {
      if (!startTime || !endTime) return directT('calendar.workingHours.notSet');
      
      const formatTime = (timeStr) => {
        const [hours, minutes] = timeStr.split(':');
        const h = parseInt(hours, 10);
        
        // Use 24-hour format for RTL languages, AM/PM for others
        if (isRtl.value) {
          return `${hours}:${minutes}`;
        } else {
          const ampm = h >= 12 ? 'PM' : 'AM';
          const hour = h % 12 || 12;
          return `${hour}:${minutes} ${ampm}`;
        }
      };
      
      // Adjust order based on RTL or LTR
      if (isRtl.value) {
        return `${formatTime(endTime)} - ${formatTime(startTime)}`;
      } else {
        return `${formatTime(startTime)} - ${formatTime(endTime)}`;
      }
    };

    // Use translated day names
    const days = computed(() => [
      { key: 'sunday', label: directT('calendar.workingHours.sunday') },
      { key: 'monday', label: directT('calendar.workingHours.monday') },
      { key: 'tuesday', label: directT('calendar.workingHours.tuesday') },
      { key: 'wednesday', label: directT('calendar.workingHours.wednesday') },
      { key: 'thursday', label: directT('calendar.workingHours.thursday') },
      { key: 'friday', label: directT('calendar.workingHours.friday') },
      { key: 'saturday', label: directT('calendar.workingHours.saturday') }
    ]);

    const closeModal = () => {
      emit('close');
    };

    const getUserInfo = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          userId.value = user.id;
          console.log("Got user ID:", userId.value);
          
          // Get organization ID using the same approach as AddEventModal
          const { data, error } = await supabase
            .from('user_profiles')
            .select('org_id')
            .eq('user_id', user.id)
            .single();
            
          if (!error && data && data.org_id) {
            orgId.value = data.org_id;
            console.log("Retrieved organization ID from user_profiles:", orgId.value);
          } else {
            console.error("Could not retrieve organization ID from user_profiles:", error);
          }
        } else {
          console.error("No user found in authentication");
        }
      } catch (error) {
        console.error('Error getting user info:', error);
      }
    };

    const fetchWorkingHours = async () => {
      if (!userId.value || !orgId.value) return;
      
      loading.value = true;
      try {
        console.log("Fetching availability for user:", userId.value, "org:", orgId.value);
        
        const { data, error } = await supabase
          .from('employee_availability')
          .select('*')
          .eq('user_id', userId.value)
          .eq('org_id', orgId.value)
          .single();
        
        if (error) {
          console.error("Supabase error while fetching working hours:", {
            message: error.message,
            code: error.code,
            details: error.details,
            hint: error.hint
          });
          throw error;
        }
        
        console.log("Received availability data:", data);
        
        if (data) {
          // Update workingHours with fetched data
          for (const key in data) {
            if (key in workingHours.value) {
              workingHours.value[key] = data[key];
            }
          }
        } else {
          console.log("No availability data found for this user");
        }
      } catch (error) {
        console.error('Error fetching working hours:', {
          name: error.name,
          message: error.message,
          stack: error.stack,
          ...(error.response ? { response: error.response } : {})
        });
      } finally {
        loading.value = false;
      }
    };

    const saveWorkingHours = async () => {
      loading.value = true;
      
      try {
        console.log("Starting saveWorkingHours with userId:", userId.value, "orgId:", orgId.value);
        
        // If user ID or org ID are missing, try to get them first
        if (!userId.value || !orgId.value) {
          console.log("Missing user ID or org ID, trying to fetch them...");
          await getUserInfo();
        }
        
        // Double check if we have the IDs after trying to fetch them
        if (!userId.value) {
          console.error('User ID not available - cannot save working hours');
          return;
        }
        
        if (!orgId.value) {
          console.error('Organization ID not available - cannot save working hours');
          return;
        }
        
        console.log("Saving working hours for user:", userId.value, "org:", orgId.value);
        
        // Check if record exists
        const { data: existingRecord, error: checkError } = await supabase
          .from('employee_availability')
          .select('id')
          .eq('user_id', userId.value)
          .eq('org_id', orgId.value)
          .single();
        
        if (checkError) {
          if (checkError.code !== 'PGRST116') { // PGRST116 means "No rows returned" which is fine
            console.error("Error checking for existing record:", {
              message: checkError.message,
              code: checkError.code,
              details: checkError.details,
              hint: checkError.hint
            });
            throw checkError;
          } else {
            console.log("No existing record found, will create new one");
          }
        } else {
          console.log("Found existing record:", existingRecord);
        }
        
        // Create a clean object with only the fields we need
        const workingHoursData = {
          user_id: userId.value,
          org_id: orgId.value,
          is_active: true,
          updated_at: new Date(),
          // Copy all day availability fields
          sunday_available: workingHours.value.sunday_available,
          sunday_start_time: workingHours.value.sunday_start_time,
          sunday_end_time: workingHours.value.sunday_end_time,
          monday_available: workingHours.value.monday_available,
          monday_start_time: workingHours.value.monday_start_time,
          monday_end_time: workingHours.value.monday_end_time,
          tuesday_available: workingHours.value.tuesday_available,
          tuesday_start_time: workingHours.value.tuesday_start_time,
          tuesday_end_time: workingHours.value.tuesday_end_time,
          wednesday_available: workingHours.value.wednesday_available,
          wednesday_start_time: workingHours.value.wednesday_start_time,
          wednesday_end_time: workingHours.value.wednesday_end_time,
          thursday_available: workingHours.value.thursday_available,
          thursday_start_time: workingHours.value.thursday_start_time,
          thursday_end_time: workingHours.value.thursday_end_time,
          friday_available: workingHours.value.friday_available,
          friday_start_time: workingHours.value.friday_start_time,
          friday_end_time: workingHours.value.friday_end_time,
          saturday_available: workingHours.value.saturday_available,
          saturday_start_time: workingHours.value.saturday_start_time,
          saturday_end_time: workingHours.value.saturday_end_time
        };
        
        console.log("Data to save:", workingHoursData);
        
        let result;
        
        if (existingRecord) {
          // Update existing record
          console.log("Updating existing record:", existingRecord.id);
          result = await supabase
            .from('employee_availability')
            .update(workingHoursData)
            .eq('id', existingRecord.id);
        } else {
          // Insert new record
          console.log("Creating new working hours record");
          workingHoursData.created_at = new Date();
          result = await supabase
            .from('employee_availability')
            .insert(workingHoursData);
        }
        
        if (result.error) {
          console.error("Error saving working hours:", {
            message: result.error.message,
            code: result.error.code,
            details: result.error.details,
            hint: result.error.hint
          });
          throw result.error;
        }
        
        console.log("Working hours saved successfully", result.data);
        closeModal();
      } catch (error) {
        console.error('Error in saveWorkingHours:', {
          name: error.name,
          message: error.message,
          stack: error.stack,
          ...(error.response ? { response: error.response } : {})
        });
      } finally {
        loading.value = false;
      }
    };

    onMounted(async () => {
      await getUserInfo();
      
      // No need for storage event listeners anymore since
      // we're using Vue I18n's reactive locale directly
    });

    watch(() => props.isActive, async (newValue) => {
      if (newValue) {
        await fetchWorkingHours();
      }
    });
    
    // No need for cleanup since we're not adding event listeners

    return {
      workingHours,
      days,
      loading,
      saveWorkingHours,
      closeModal,
      formatWorkingHours,
      directT,
      isRtl
    };
  }
};
</script>

<style scoped>
/* Basic toggle styling */
input[type="checkbox"] + label div div {
  transition: left 0.3s ease;
}

/* Better time input appearance */
input[type="time"] {
  cursor: pointer;
}

input[type="time"]::-webkit-calendar-picker-indicator {
  filter: invert(50%);
  cursor: pointer;
}

/* Custom transition */
.border-l-green-500, .border-l-gray-300 {
  transition: all 0.3s ease;
}

.bg-green-500 {
  background-color: #10b981 !important;
}

.bg-blue-500 {
  background-color: #3b82f6 !important;
}

.bg-blue-600 {
  background-color: #2563eb !important;
}

.bg-red-500 {
  background-color: #ef4444 !important;
}

.bg-red-600 {
  background-color: #dc2626 !important;
}

.add-new-popup {
  opacity: 0;
  visibility: hidden;
  background-color: rgba(0, 0, 0, 0.4);
}
.add-new-popup.active {
  opacity: 1;
  visibility: visible;
}
.popup-dialog {
  opacity: 0;
  visibility: hidden;
  transform: scale(0.8);
}
.add-new-popup.active .popup-dialog {
  opacity: 1;
  visibility: visible;
  transform: scale(1);
}
</style> 